<template>

	<v-row>
		<v-col>

			<v-form
					v-model="formIsValid.inviteDetails"
					ref="inviteDetailsForm"
					class="mb-8"
			>
				<ui-box eager :form="$refs.inviteDetailsForm" new-forms>
					<form-title title="Invite Customization">
						Your message will be incorporated into the email sequence sent to
						places that have an associated contact. It will also be visible within
						Bandwango's Merchant Portal, enabling your places to make an informed
						decision when joining your pass.
					</form-title>

					<form-divider inner/>

					<v-row>
						<v-col cols="12" sm="6">

							<v-text-field
								v-model="mainCampaign.createdByName"
								class="flex-grow-1 rounded-lg"
								disabled
								hide-details="auto"
								label="Created by"
								outlined
							></v-text-field>
						</v-col>
						<v-col cols="12" sm="6">
							<v-text-field
								v-model="mainCampaign.email_settings.reply_to"
								class="flex-grow-1 rounded-lg"
								:hide-details="false"
								label="Reply To Email"
								persistent-hint
								:rules="rules.replyTo"
								outlined
							></v-text-field>
						</v-col>
					</v-row>
					<v-row>
						<v-col cols="12" sm="6">
							<bw-date-time-picker
									v-model="localPass.launch_date"
									class="rounded-lg"
									:hide-details="false"
									hint="This date will be included on the invitation so that the Place knows when the pass will launch"
									label="Anticipated Launch Date"
									persistent-hint
									required
									:rules="rules.launch_date"
							></bw-date-time-picker>
						</v-col>
					</v-row>
					<v-row>
						<v-col cols="12" sm="6">
							<bw-date-time-picker
                  v-if="mainCampaign?.email_settings.dripSequences[0]"
									v-model="mainCampaign?.email_settings.dripSequences[0].activationDate"
									class="rounded-lg"
									:hide-details="false"
									hint="Date that invites will start sending to Places"
									label="Start Sending Invites On"
									persistent-hint
									with-time
							></bw-date-time-picker>
						</v-col>
						<v-col cols="12" sm="6">
							<bw-date-time-picker
									v-model="mainCampaign.end_date"
									class="rounded-lg"
									:hide-details="false"
									hint="This is the cutoff date for Places to join the pass and/or submit products."
									label="Stop Sending Invites On"
									persistent-hint
									with-time
							></bw-date-time-picker>
						</v-col>
					</v-row>
				</ui-box>
			</v-form>

			<form-divider/>

			<v-form
					v-model="formIsValid.description"
					ref="descriptionForm"
					class="mb-8"
			>
				<ui-box
						:form="false"
						new-forms
						:status="null"
				>
					<form-title title="Call Outreach Details">
						Provide key details to support our team in reaching out
						to merchants and ensuring a smooth onboarding experience.
					</form-title>

					<form-divider inner/>

					<v-row>
						<v-col>
							<v-switch
								v-model="localPass.pass_settings.call"
								class="mt-0"
								dense
								:false-value="false"
								hide-details="auto"
								inset
								label="Request Bandwango calls to merchants"
								:true-value="true"
								@change="callChanged"
							></v-switch>
						</v-col>
					</v-row>
					<template v-if="localPass.pass_settings.call">
						<v-row>
							<v-col>
								<v-textarea
										v-model="localPass.target_audience"
										class="rounded-lg"
										clearable
										counter="2000"
										label="Describe the target audience for this pass"
										maxlength="2000"
										outlined
										placeholder="How should we describe the target audience(s) that this pass is likely to attract to prospective merchants"
										rows="4"
										type="text"
								></v-textarea>
							</v-col>
						</v-row>
						<v-row>
							<v-col>
								<v-textarea
										v-model="localPass.join_reason"
										class="rounded-lg"
										clearable
										counter="2000"
										label="Why should merchants join?"
										maxlength="2000"
										outlined
										placeholder="What information can we communicate to merchants to help convince merchants to join this pass?"
										rows="4"
										type="text"
								></v-textarea>
							</v-col>
						</v-row>
					</template>
				</ui-box>
			</v-form>

			<form-divider/>

			<ui-box :status="hasPlaceWithoutContact ? 'warning' : 'success'" new-forms>
				<form-title title="Places">
					Below you will see all of the places in your pass and what their status is.
				</form-title>

				<form-divider inner/>

				<v-row>
					<v-col>

						<!-- PlacesTable -->
						<bw-table
								aria-label="data-table"
								:headers="placesHeaders"
								:hide-default-footer="!localPlaces.length"
								:hide-default-header="!localPlaces.length"
								hide-row-select
								hide-search
								id="product-req-table"
								:items="localPlaces"
						>
							<template v-slot:no-data>
								<div
										class="align-center d-flex justify-center w-100"
										style="height: 47px;"
								>
									Oops, no places to go yet!
								</div>
							</template>

							<template v-slot:item="{ item }">
								<tr :class="{
									'mark-complete': !isPointOfInterest(item) && item.totalUsers,
									// 'mark-disabled': isPointOfInterest(item),
									'mark-incomplete': !isPointOfInterest(item) && !item.totalUsers
								}">
									<td>
										<v-img
												aspect-ratio="2"
												cover
												class="rounded"
												:key="item.media[0].id"
												lazy-src="https://placehold.co/400x300?text=Loading"
												max-width="120"
												:src="item.image?.url || '/images/placehold.co.png'"
												:width="$vuetify.breakpoint.smAndUp ? '5vw' : '10vw'"
										></v-img>
									</td>
									<td>
										<div class="text-subtitle-2">
											{{ item.name }}
										</div>
										<div v-if="item?.overwrittenNames?.length > 0" class="text-subtitle-3 font-italic">
											<div v-for="(overwrittenName) in item.overwrittenNames" :key="`overwritten-name-${overwrittenName}`">
												{{ overwrittenName }}
											</div>
										</div>
										<div class="grey--text mb-2 text-body-2 text--darken-1">
											{{ item.formatted_address }}
										</div>
									</td>
									<td>
										<div v-if="isPointOfInterest(item)">
											<v-icon size="24">
												mdi-check
											</v-icon>
										</div>
									</td>
									<td>
										<div class="mt-1 text-no-wrap">
											{{ getStatus(item.statusName) }}
										</div>
									</td>
									<td>
										<!-- NON-POI - Add Contact for Invite -->
										<div
												 
												class="text-no-wrap"
												:class="{
														'success--text': item.totalUsers,
														'warning--text': !item.totalUsers
													}"
										>
											<div class="text--darken-4 text-subtitle-2">
												{{
													!item.totalUsers
															? 'No Contact'
															: `${item.totalUsers} Contact${item.totalUsers > 1
																	? 's'
																	: ''}`
												}}
											</div>
											<div
													class="mb-2 text-body-2 text--darken-3">
												{{
													!item.totalUsers ? 'Add contact to continue.' : 'Ready to go!'
												}}
											</div>
										</div>
										<!-- END: NON-POI -->
									</td>
									<td>
										<div class="d-flex flex-nowrap">
											<v-btn
													v-if="!item.totalUsers"
													class="mt-2"
													color="warning"
													depressed
													fab
													outlined
													small
													@click="addNewContact(item)"
											>
												<v-icon>
													mdi-account-plus
												</v-icon>
											</v-btn>
											<v-btn
													v-else
													class="mt-2"
													color="success"
													depressed
													disabled
													fab
													outlined
													small
											>
												<v-icon>
													mdi-account-check
												</v-icon>
											</v-btn>

											<!-- ADMIN - Link into Merchant Portal -->
											<v-btn
													v-if="user.is_admin"
													class="ms-2 mt-2"
													color="secondary"
													depressed
													fab
													:href="getMpLink(item)"
													outlined
													small
													target="_blank"
											>
												MP
											</v-btn>
											<!-- END: ADMIN -->
										</div>
									</td>
								</tr>
							</template>
						</bw-table>
						<!-- End: Places Table -->

					</v-col>
				</v-row>

			</ui-box>

			<save-form
					:refs="$refs"
					:is-saving="isSaving"
					new-forms
					save-text="Save"
					@cancel-action="exit"
					@save-action="save"
			></save-form>

			<v-dialog
					v-if="newContact"
					v-model="newContact"
					content-class="rounded-xl"
					persistent
					scrollable
					width="600px"
			>
				<v-form v-model="formIsValid.contact" ref="frm">
					<v-card class="position-relative" rounded="xl">
						<v-card-title class="d-flex justify-space-between ma-0 ma-md-4">
							<div class="w-100">
								<div class="align-center d-flex justify-space-between">
									<h6>New Contact</h6>
									<v-btn
											class="mt-n1 me-n2"
											color="black"
											depressed
											icon
											@click="cancelNewContact"
									>
										<v-icon>mdi-close</v-icon>
									</v-btn>
								</div>
							</div>
						</v-card-title>
						<v-card-text class="text-body-2">
							<v-row>
								<v-col>
									<v-text-field
											v-model="newContact.first_name"
											class="rounded-lg"
											hide-details="auto"
											label="First Name"
											outlined
									></v-text-field>
								</v-col>
								<v-col>
									<v-text-field
											v-model="newContact.last_name"
											class="rounded-lg"
											hide-details="auto"
											label="Last Name"
											outlined
									></v-text-field>
								</v-col>
							</v-row>
							<v-row>
								<v-col>
									<v-text-field
											v-model="newContact.email"
											class="flex-grow-1 required rounded-lg"
											hide-details="auto"
											label="Email"
											outlined
											:rules="rules.email"
									></v-text-field>
								</v-col>
							</v-row>
							<v-row>
								<v-col>
									<vue-tel-input-vuetify
											ref="phone"
											v-model="newContact.phone"
											append-icon="mdi-menu-down"
											class="flex-grow-1 rounded-lg"
											clearable
											:defaultCountry="'us'"
											hide-details="auto"
											label="Phone"
											mode="international"
											outlined
											:preferred-countries="['us', 'ca']"
											:rules="rules.phone"
											@input="phoneInputEvent"
											@keydown="phoneKeyEvent"
									></vue-tel-input-vuetify>
								</v-col>
							</v-row>
						</v-card-text>

						<v-card-actions>
							<div class="align-center d-flex justify-space-between w-100">
								<span></span>
								<div class="d-flex justify-end">
									<v-btn
											class="me-2 me-md-4 rounded-lg"
											color="grey darken-1"
											depressed
											:large="$vuetify.breakpoint.mdAndUp"
											text
											@click="cancelNewContact"
									>
										Cancel
									</v-btn>
									<v-btn
											class="rounded-lg"
											color="primary"
											depressed
											:disabled="!formIsValid.contact"
											:large="$vuetify.breakpoint.mdAndUp"
											@click="saveNewContact"
									>
										Save
									</v-btn>
								</div>
							</div>
						</v-card-actions>
					</v-card>
				</v-form>
			</v-dialog>

			<v-dialog
					v-model="dialog.message"
					content-class="rounded-xl"
					persistent
					scrollable
					width="1000px"
			>
				<v-form v-model="formIsValid.message" ref="formMessage">
					<v-card class="position-relative" rounded="xl">
						<v-card-title class="d-flex justify-space-between ma-0 ma-md-4">
							<div class="w-100">
								<div class="align-center d-flex justify-space-between">
									<h6>Email Preview</h6>
									<v-btn
											class="mt-n1 me-n2"
											color="black"
											depressed
											icon
											@click="hideMessagePreview"
									>
										<v-icon>mdi-close</v-icon>
									</v-btn>
								</div>
							</div>
						</v-card-title>
						<v-card-text v-if="dialog.messageLoading" class="text-body-2">
							<v-row>
								<v-col>
									<v-container class="text-center">
										<v-progress-circular
												indeterminate
												color="primary"
										></v-progress-circular>
									</v-container>
								</v-col>
							</v-row>
						</v-card-text>
						<v-card-text v-if="!dialog.messageLoading" class="text-body-2">
							<v-row>
								<v-col>
									<v-text-field
											v-model="previewMessage.subject"
											class="rounded-lg"
											hide-details="auto"
											label="Subject"
											outlined
											readonly
									></v-text-field>
								</v-col>
							</v-row>
							<v-row>
								<v-col>
									<v-textarea
											v-model="previewMessage.message"
											class="rounded-lg"
											hide-details="auto"
											label="Message"
											outlined
											rows="12"
											type="text"
											readonly
									></v-textarea>
								</v-col>
							</v-row>
						</v-card-text>

						<v-card-actions>
							<div class="align-center d-flex justify-space-between w-100">
								<span></span>
								<div class="d-flex justify-end">
									<v-btn
											class="me-2 me-md-4 rounded-lg"
											color="grey darken-1"
											depressed
											:large="$vuetify.breakpoint.mdAndUp"
											outlined
											@click="hideMessagePreview"
									>
										Close
									</v-btn>
								</div>
							</div>
						</v-card-actions>
					</v-card>
				</v-form>
			</v-dialog>

			<confirm-dialog
				:cancelText="'No'"
				:confirmText="'Yes'"
				:open="dialog.campaignCall"
				description="Would you like to request that Bandwango's team call these merchants to support your onboarding efforts?"
				title="Call Merchants"
				@cancel="callCampaign(false)"
				@confirm="callCampaign(true)"
			></confirm-dialog>

		</v-col>
	</v-row>

</template>

<script>
import {Campaign} from '../../../JSON/Entities';
import {Place} from '../../../helpers';
import ConfirmDialog from '@app/Shared/UI/ConfirmDialog.vue';

export default {
	name: "Invites",
	components: {
		ConfirmDialog
	},
	props: {
		partner: {
			type: Object,
			default: () => ({})
		},
		pass: {
			type: Object,
			default: () => ({})
		},
		places: {
			type: Array,
			default: () => []
		},
		user: {
			type: Object,
			default: () => ({})
		}
	},
	data() {
		return {
			addingContactToPlace: null,
			dialog: {
				campaignCall: false,
				contact: false,
				message: false,
				messageLoading: false
			},
			formIsValid: {
				contact: false,
				inviteDetails: false,
				message: false
			},
			previewMessage: {
				subject: null,
				message: null
			},
			isSaving: false,
			localPass: this.pass,
			localPlaces: [],
			mainCampaign: {},
			newContact: null,
			newContactUrl: null,
			placesHeaders: [
				{
					align: 'start',
					mandatory: true,
					text: 'Image',
					value: 'media',
					width: '1%'
				},
				{
					align: 'start',
					mandatory: true,
					text: 'Name/Address',
					value: 'name'
				},
				{
					align: 'start',
					mandatory: true,
					text: 'POI',
					sortable: false,
					width: '1%'
				},
				{
					align: 'start',
					mandatory: true,
					text: 'Status',
					sortable: false,
					value: 'statusName',
					width: '1%'
				},
				{
					align: 'start',
					mandatory: true,
					text: 'Actions',
					sortable: false,
					value: 'actions',
					width: '1%'
				},
				{
					align: 'start',
					mandatory: true,
					text: '',
					sortable: false,
					width: '1%'
				}
			],
			rules: {
				email: [
					v => !!v || 'Email is required.',
					v => v !== this.user.email || 'You cannot enter your own email as the contact.',
					v => {
						const pattern =
								// eslint-disable-next-line max-len
								/^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
						return pattern.test(v) || 'Please enter a valid e-mail.';
					}
				],
				replyTo: [
					v => {
						const pattern =
							// eslint-disable-next-line max-len
							/^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
						return !v || pattern.test(v) || 'Please enter a valid e-mail.';
					}
				],
				invite_date: [
					// This will allow empty, but validate if you start typing
					// v => (!v || (!!v && this.phoneIsValid)) || 'Please enter a valid phone.'
				],
				phone: [
					v => !v || v && this.phoneIsValid || 'Please enter a valid phone or leave blank.'
				],
				launch_date: [v => !!v || 'Launch date is required.'],
				invites_start_date: [v => !!v || 'Invites start date is required.']
			}
		};
	},
	created() {
		this.places.forEach(place => this.localPlaces.push(new Place(place)));

		// this.mainCampaign = cloneDeep(this.pass.campaigns[0] || {});
		this.mainCampaign = new Campaign(this.pass.campaigns[0] || {});

		// const emailSettings = {
		// 	dripSequences: [
		// 		{
		// 			activationDate: ''
		// 		}
		// 	],
		// 	...this.mainCampaign.email_settings,
		// };
		//
		// this.$set(this.mainCampaign, 'email_settings', emailSettings);

		// this.localCampaigns = this.pass.campaigns && this.pass.campaigns.length > 0
		// 		? this.pass.campaigns.map(campaign => {
		// 			// Deep clone the campaign object
		// 			let newCampaign = cloneDeep(campaign);
		//
		// 			// Ensure email_settings exists and is not null
		// 			if(!newCampaign.email_settings) {
		// 				newCampaign.email_settings = {}; // Initialize if null
		// 			}
		//
		// 			// Ensure email_settings.dripSequences exists and is not null
		// 			if(!newCampaign.email_settings.dripSequences) {
		// 				newCampaign.email_settings.dripSequences = []; // Initialize if null
		// 			}
		//
		// 			// Check if dripSequences array has at least one item, if not, add an object with activationDate
		// 			if(newCampaign.email_settings.dripSequences.length === 0) {
		// 				newCampaign.email_settings.dripSequences.push({ activationDate: '' }); // Initialize with empty activationDate
		// 			}
		// 			else {
		// 				// Ensure each item in dripSequences has an activationDate property
		// 				newCampaign.email_settings.dripSequences = newCampaign.email_settings.dripSequences.map(sequence => {
		// 					if(sequence.activationDate === undefined) {
		// 						sequence.activationDate = ''; // Set activationDate to '' if it doesn't exist
		// 					}
		// 					return sequence;
		// 				});
		// 			}
		//
		// 			return newCampaign;
		// 		})
		// 		: [];

		window.Echo.channel('partner-portal')
				.listen('.CampaignStatusChanged', (e) => {
					this.handleStatusChange(e);
				});
	},
	mounted() {
		if(!('call' in this.localPass?.pass_settings)) {
			this.dialog.campaignCall = true;
		}
	},
	computed: {
		hasPlaceWithoutContact() {
			return this.localPlaces
					.filter(place => !this.isPointOfInterest(place))
					.some(place => place.totalUsers === 0);
		},
		// mainCampaign() {
		// 	return new Campaign(this.localCampaigns[0] || null);
		// }
	},
	methods: {
		addNewContact(place) {
			this.addingContactToPlace = place;
			this.newContactUrl = `/partner-portal/${this.partner.id}/users/create-first-venue-user/${place.id}`;
			this.newContact = new Contact();
		},

		callCampaign(doCall) {
			this.dialog.messageLoading=true;
			this.dialog.campaignCall=false;
			this.$set(this.localPass.pass_settings, 'call', doCall);
			const url = `/partner-portal/${this.partner.id}/passes/update-call/${this.localPass.id}`;
			axios.post(url, {
				partnerId: this.partner.id,
				doCall: doCall,
			})
				.then(response => {
					this.dialog.messageLoading = false;
				})
				.catch(error => {
					this.dialog.messageLoading = false;
					window.EventBus.$emit('system-messages', [
						{ message: error.message, messageType: 'error' }
					]);
				});
		},

		callChanged() {
			this.localPass.campaigns.forEach(campaign => {
				campaign.call = this.localPass.pass_settings.call;
			});
			this.mainCampaign.call = this.localPass.pass_settings.call;
		},
		
		cancelNewContact() {
			this.addingContactToPlace = null;
			this.newContactUrl = null;
			this.newContact = null;
		},

		exit() {
			window.location.href = `/partner-portal/${this.partner.id}/passes`;
		},

		getMpLink(item) {
			const campaign = this.pass?.campaigns?.find(campaign =>
					(campaign?.venues ?? []).some(venue => venue.id === item.id)
			);

			return campaign
					? `/merchant-portal/${item.id}/invites/view/landing/${campaign.id}`
					// ? `/merchant-portal/${item.id}/invites/view/invite/${campaign.id}`
					: 'javascript:void(0);';

		},

		getStatus(statusName) {
			if(statusName === 'Live') return 'Approved';
			if(statusName === 'Setup') return 'Added';
			return statusName;
		},

		handleStatusChange(data) {
			const place = this.localPlaces.find(p => p.id === data.id);

			if(place) {
				// Update the place properties reactively
				this.$set(place, 'status', data.status);
				this.$set(place, 'statusName', data.statusName);
			}
			else {
				// Handle the case where the place is not found
				console.warn(`Place with ID ${data.id} not found.`);
			}
		},

		hasNonPointOfInterestPlace() {
			return this.localPlaces.some(place => !this.isPointOfInterest(place));
		},

		isPointOfInterest(place) {
			return place?.types?.length === 1 && place.types[0] === 'point_of_interest';
		},

		phoneInputEvent(formattedNumber, { valid }) {
			// Only flip the form validity to false if NOT valid, do nothing on valid = true
			if(!valid && formattedNumber) this.formIsValid.editPlace = false;

			// For field validation rule
			this.phoneIsValid = valid;
		},

		phoneKeyEvent(e) {
			const validCharsRegex = new RegExp(/^[0-9 +\-()]*$/);

			// Allow Control+V or Command+V for pasting
			const isPasteOperation = (e.key === 'v' || e.key === 'V') && (e.ctrlKey || e.metaKey);

			if(!validCharsRegex.test(e.key) && ![
				'ArrowLeft',
				'ArrowRight',
				'Backspace',
				'Delete',
				'Tab'
			].includes(e.key) && !isPasteOperation) {
				e.preventDefault();
			}
		},

		showMessagePreview(messageWithVariables) {
			this.dialog.messageLoading = true;
			this.dialog.message = true;

			const url = `/partner-portal/${this.partner.id}/passes/get-message-content`;
			axios.post(url, {
				subject: messageWithVariables.subject,
				message: messageWithVariables.message,
				dealId: this.pass.id,
				campaignId: this.mainCampaign?.id,
				venueId: this.localPlaces[0]?.id,
				launchDate: this.pass.launch_date,
				endDate: this.mainCampaign?.end_date,
			})
					.then(response => {
						this.previewMessage.subject = response.data.subject
						this.previewMessage.message = response.data.message
						this.dialog.messageLoading = false;
					})
					.catch(error => {
						this.dialog.messageLoading = false;
						window.EventBus.$emit('system-messages', [
							{ message: error.message, messageType: 'error' }
						]);
					});
		},

		hideMessagePreview() {
			this.dialog.message = false;
			this.previewMessage.subject = null;
			this.previewMessage.message = null;
		},

		save() {
			this.isSaving = true;
			const url = `/partner-portal/${this.partner.id}/passes/save-campaigns/${this.localPass.id || ''}`;

			const campaigns = cloneDeep(this.localPass.campaigns);
			campaigns[0] = this.mainCampaign;

			axios.put(url, { pass: this.pass, campaigns: campaigns })
					.then(({ data }) => {
						window.location = `/partner-portal/${this.partner.id}/passes/${this.localPass.id}/submissions`;
					})
					.catch(error => {
						this.isSaving = false;
						window.EventBus.$emit('system-messages', [
							{ message: error.message, messageType: 'error' }
						]);
					});
		},

		saveNewContact() {
			axios.put(this.newContactUrl, this.newContact)
					.then(() => {
						this.localPlaces.map(place => {
							if(place.id === this.addingContactToPlace.id)
								place.totalUsers = 1;
						});
						this.cancelNewContact();
						window.EventBus.$emit('get-updated-menu');
					})
					.catch(error => {
						this.isSaving = false;
						window.EventBus.$emit('system-messages', [
							{ message: error.message, messageType: 'error' }
						]);
					});
		}
	}
}

// function Campaign(campaign) {
// 	this.description = null;
// 	this.email_settings = {
// 		dripSequences: [
// 			{
// 				activationDate: null
// 			}
// 		]
// 	};
// 	this.launch_date = null;
//
// 	// Assuming campaign is an object with properties you want to assign to 'this'
// 	if(campaign) {
// 		// You can iterate over the properties of campaign and assign them to 'this'
// 		for (var key in campaign) {
// 			if(campaign.hasOwnProperty(key)) {
// 				this[key] = campaign[key];
// 			}
// 		}
// 	}
//
// }

function Contact() {
	this.first_name = null;
	this.last_name = null;
	this.email = null;
	this.phone = null;
}
</script>

<style lang="scss" scoped>
::v-deep #product-req-table td {
	vertical-align: top !important;
}

@keyframes bounce {
	0%, 20%, 100% {
		transform: translateY(0);
	}
	10% {
		transform: translateY(-20px);
	}
}

.bounce {
	animation: bounce 0.5s cubic-bezier(0.68, -0.55, 0.27, 1.55) infinite;
	animation-delay: 2s; /* 2s gap before the animation starts */
	animation-timing-function: steps(3, end);
}
</style>
